/**
 * batch-67 campagnes (FO-503) — evenement-campagne (kerstborrel): gedeelde
 * enums, request-schema's (API `.safeParse` + inline veldfouten in de portal-
 * wizard) en DTO's voor portal (eigen aanmelding) en backoffice (beheer,
 * tellers, lijst, export).
 *
 * Privacy (§D9): namen en leeftijden van introducees staan alleen in de
 * organisatie-DTO (`EvenementAanmeldingRijDTO.introducees`), die de API
 * uitsluitend aan de organisatierol (HR/ADMIN) geeft; drie maanden na het
 * evenement anonimiseert de cron ze. De medewerker ziet alleen zijn eigen
 * aanmelding.
 */
import { z } from 'zod'
import type { CampagneDeelnameStatus } from './campagne'

export const EVENEMENT_THEMA = ['KERST', 'GEEN'] as const
export type EvenementThema = (typeof EVENEMENT_THEMA)[number]

export const EVENEMENT_THEMA_LABELS: Record<EvenementThema, string> = {
  KERST: 'Kerst',
  GEEN: 'Geen thema',
}

export const EVENEMENT_INTRODUCEE_RELATIE = ['PARTNER', 'KIND', 'ANDERS'] as const
export type EvenementIntroduceeRelatie = (typeof EVENEMENT_INTRODUCEE_RELATIE)[number]

export const EVENEMENT_INTRODUCEE_RELATIE_LABELS: Record<EvenementIntroduceeRelatie, string> = {
  PARTNER: 'Partner',
  KIND: 'Kind',
  ANDERS: 'Anders',
}

/** Leeftijdsgroepen voor catering en cadeaus (§D8). */
export const EVENEMENT_LEEFTIJDSGROEPEN = [
  { key: 'kinderen0tm3', label: '0 t/m 3', van: 0, tot: 3 },
  { key: 'kinderen4tm8', label: '4 t/m 8', van: 4, tot: 8 },
  { key: 'kinderen9tm12', label: '9 t/m 12', van: 9, tot: 12 },
  { key: 'kinderen13tm17', label: '13 t/m 17', van: 13, tot: 17 },
] as const
export type EvenementLeeftijdsgroepKey = (typeof EVENEMENT_LEEFTIJDSGROEPEN)[number]['key']

export function leeftijdsgroepVan(leeftijd: number): EvenementLeeftijdsgroepKey | null {
  const g = EVENEMENT_LEEFTIJDSGROEPEN.find((x) => leeftijd >= x.van && leeftijd <= x.tot)
  return g?.key ?? null
}

/** Default maximum aantal introducees per medewerker (instelbaar per evenement). */
export const EVENEMENT_MAX_INTRODUCEES_DEFAULT = 10
export const EVENEMENT_DIEETWENSEN_MAX = 200

// ─── Request-schema's ────────────────────────────────────────────────────────

const naamSchema = z.string().trim().min(1, 'Vul een naam in').max(80)

export const evenementIntroduceeSchema = z
  .object({
    naam: naamSchema,
    relatie: z.enum(EVENEMENT_INTRODUCEE_RELATIE),
    leeftijd: z.number().int().min(0).max(17).nullable().optional(),
  })
  .superRefine((d, ctx) => {
    if (d.relatie === 'KIND' && (d.leeftijd === null || d.leeftijd === undefined)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['leeftijd'], message: 'Vul de leeftijd van het kind in (0 t/m 17)' })
    }
  })
export type EvenementIntroduceeInput = z.infer<typeof evenementIntroduceeSchema>

/** Aanmelden/wijzigen door de medewerker (portal-wizard). */
export const evenementAanmeldenSchema = z
  .object({
    aanwezig: z.boolean(),
    afwezigReden: z.string().trim().max(200).optional().nullable().transform((v) => (v ? v : null)),
    introducees: z.array(evenementIntroduceeSchema).max(50).default([]),
    dieetwensen: z
      .string()
      .trim()
      .max(EVENEMENT_DIEETWENSEN_MAX, `Maximaal ${EVENEMENT_DIEETWENSEN_MAX} tekens`)
      .optional()
      .nullable()
      .transform((v) => (v ? v : null)),
  })
  .superRefine((d, ctx) => {
    if (!d.aanwezig && d.introducees.length > 0) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['introducees'], message: 'Zonder aanwezigheid kun je niemand meenemen' })
    }
  })
export type EvenementAanmeldenInput = z.infer<typeof evenementAanmeldenSchema>

/** Aanmaken/bewerken van een evenement (backoffice HRM). */
const evenementBeheerBasis = z
  .object({
    titel: z.string().trim().min(1, 'Vul een titel in').max(120),
    /** ISO-instant van het begin (NL-tijd aan de rand). */
    datum: z.string().datetime(),
    eindTijd: z.string().datetime().nullable().optional(),
    locatie: z.string().trim().max(200).nullable().optional().transform((v) => (v ? v : null)),
    omschrijving: z.string().trim().max(2000).nullable().optional().transform((v) => (v ? v : null)),
    thema: z.enum(EVENEMENT_THEMA).default('KERST'),
    /** Geplande vrijgave (ISO); leeg = alleen handmatig vrijgeven. */
    vrijgaveDatum: z.string().datetime().nullable().optional(),
    /** Antwoorddeadline (ISO), default 1 december voor het evenement. */
    antwoordDeadline: z.string().datetime(),
    maxIntroducees: z.number().int().min(0).max(50).default(EVENEMENT_MAX_INTRODUCEES_DEFAULT),
    entityIds: z.array(z.string()).default([]),
    departmentIds: z.array(z.string()).default([]),
  })
const NL_DAG = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam', year: 'numeric', month: '2-digit', day: '2-digit' })

/**
 * NL-kalenderdag ('YYYY-MM-DD') van een instant. De antwoorddeadline is een DAG
 * (tot en met): "op of voor de evenementdatum" vergelijkt dus dagen. Als instants
 * vergeleken viel een deadline op de dag van het evenement zelf altijd af (het
 * einde van de dag ligt na de begintijd), terwijl de melding "op" belooft.
 */
export function evenementNlDag(moment: string | Date): string {
  return NL_DAG.format(new Date(moment))
}

/** Ligt de antwoorddeadline op of voor de dag van het evenement? */
export function isAntwoordDeadlineOpOfVoorEvenement(antwoordDeadline: string | Date, datum: string | Date): boolean {
  return evenementNlDag(antwoordDeadline) <= evenementNlDag(datum)
}

export const evenementBeheerSchema = evenementBeheerBasis
  .refine((d) => isAntwoordDeadlineOpOfVoorEvenement(d.antwoordDeadline, d.datum), {
    message: 'De antwoorddeadline moet op of voor de evenementdatum liggen',
    path: ['antwoordDeadline'],
  })
  .refine((d) => !d.eindTijd || new Date(d.eindTijd) > new Date(d.datum), {
    message: 'De eindtijd moet na de begintijd liggen',
    path: ['eindTijd'],
  })
  .refine((d) => !d.vrijgaveDatum || new Date(d.vrijgaveDatum) <= new Date(d.antwoordDeadline), {
    message: 'De geplande vrijgave moet op of voor de antwoorddeadline liggen',
    path: ['vrijgaveDatum'],
  })
export type EvenementBeheerInput = z.infer<typeof evenementBeheerSchema>

/**
 * Wijzigen: elk veld optioneel, ZONDER default en ZONDER omzetting. Afwezig
 * betekent "ongewijzigd"; `null` of een lege tekst betekent "leegmaken".
 *
 * Bewust geen `evenementBeheerBasis.partial()`: Zod 4 past een default ook toe op
 * een veld dat ontbreekt, en de omzetting leeg->null draait dan ook. Een
 * wijziging van alleen de titel zou het thema terugzetten, de doelgroep
 * leegmaken en de locatie en omschrijving wissen. De samenhang tussen de datums
 * toetst de service op de SAMENGEVOEGDE waarden (opgeslagen plus gewijzigd).
 */
export const evenementBeheerUpdateSchema = z.object({
  titel: z.string().trim().min(1, 'Vul een titel in').max(120).optional(),
  datum: z.string().datetime().optional(),
  eindTijd: z.string().datetime().nullable().optional(),
  locatie: z.string().trim().max(200).nullable().optional(),
  omschrijving: z.string().trim().max(2000).nullable().optional(),
  thema: z.enum(EVENEMENT_THEMA).optional(),
  vrijgaveDatum: z.string().datetime().nullable().optional(),
  antwoordDeadline: z.string().datetime().optional(),
  maxIntroducees: z.number().int().min(0).max(50).optional(),
  entityIds: z.array(z.string()).optional(),
  departmentIds: z.array(z.string()).optional(),
})
export type EvenementBeheerUpdateInput = z.infer<typeof evenementBeheerUpdateSchema>

// ─── DTO's ───────────────────────────────────────────────────────────────────

export interface EvenementTellersDTO {
  doelgroep: number
  ja: number
  nee: number
  nogNiet: number
  volwassenen: number
  kinderen: number
  kinderen0tm3: number
  kinderen4tm8: number
  kinderen9tm12: number
  kinderen13tm17: number
  metDieetwensen: number
}

export interface EvenementBeheerDTO {
  id: string
  campagneId: string
  titel: string
  datum: string
  eindTijd: string | null
  locatie: string | null
  omschrijving: string | null
  thema: EvenementThema
  status: 'CONCEPT' | 'OPEN' | 'GESLOTEN' | 'AFGEROND'
  /**
   * Geplande vrijgave; null = alleen handmatig vrijgeven (knop "Vrijgeven").
   * Na het vrijgeven staat het werkelijke moment in `vrijgegevenOp`.
   */
  vrijgaveDatum: string | null
  vrijgegevenOp: string | null
  antwoordDeadline: string
  maxIntroducees: number
  entityIds: string[]
  departmentIds: string[]
  anonimiseerOp: string | null
  geanonimiseerdOp: string | null
  tellers: EvenementTellersDTO
}

export interface EvenementIntroduceeDTO {
  id: string
  naam: string
  relatie: EvenementIntroduceeRelatie
  leeftijd: number | null
}

/** Backoffice-lijst (organisatie). `introducees` alleen voor de organisatierol; anders leeg met `introduceesVerborgen`. */
export interface EvenementAanmeldingRijDTO {
  deelnameId: string
  employeeId: string
  employeeNaam: string
  entityNaam: string | null
  departmentNaam: string | null
  status: CampagneDeelnameStatus
  aanwezig: boolean | null
  afwezigReden: string | null
  aantalVolwassenen: number
  aantalKinderen: number
  dieetwensen: string | null
  bevestigdOp: string | null
  gewijzigdOp: string | null
  /** De aanmelding is door HR namens de medewerker vastgelegd of gewijzigd. */
  doorHr: boolean
  introducees: EvenementIntroduceeDTO[]
  introduceesVerborgen: boolean
  geanonimiseerd: boolean
}

/** Portal: het evenement + de eigen aanmelding. */
export interface MijnEvenementDTO {
  campagneId: string
  titel: string
  datum: string
  eindTijd: string | null
  locatie: string | null
  omschrijving: string | null
  thema: EvenementThema
  antwoordDeadline: string
  dagenResterend: number
  maxIntroducees: number
  status: 'CONCEPT' | 'OPEN' | 'GESLOTEN' | 'AFGEROND'
  /** Wijzigen kan tot de deadline; daarna alleen via HR. */
  wijzigbaar: boolean
  deelnameId: string | null
  deelnameStatus: CampagneDeelnameStatus | null
  aanmelding: {
    aanwezig: boolean
    afwezigReden: string | null
    dieetwensen: string | null
    introducees: EvenementIntroduceeDTO[]
    bevestigdOp: string
    /** HR heeft de aanmelding namens de medewerker vastgelegd of gewijzigd. */
    doorHr: boolean
  } | null
}

export interface EvenementAanmeldResultaatDTO {
  mijn: MijnEvenementDTO
  samenvatting: string
}

/**
 * Samenvatting zoals in stap 3 en in de bevestiging, altijd in de tweede persoon
 * (de lezer is de medewerker zelf): "Jij + Sanne (partner) + Mila (kind, 6)".
 */
export function evenementSamenvatting(
  aanwezig: boolean,
  introducees: ReadonlyArray<{ naam: string; relatie: EvenementIntroduceeRelatie; leeftijd?: number | null }>,
): string {
  if (!aanwezig) return 'Je bent er niet bij'
  const delen = introducees.map((i) => {
    const rel = EVENEMENT_INTRODUCEE_RELATIE_LABELS[i.relatie].toLowerCase()
    return i.relatie === 'KIND' && i.leeftijd != null ? `${i.naam} (${rel}, ${i.leeftijd})` : `${i.naam} (${rel})`
  })
  return ['Jij', ...delen].join(' + ')
}

/**
 * Samenvatting voor de MELDING (bel, mail, push): alleen aantallen, geen namen en
 * geen leeftijden. Wat in een melding staat blijft bewaard in de meldingen- en de
 * mailtabel, en die vallen buiten de anonimisering van drie maanden na het
 * evenement (FO-503 §D9). De namen staan op de pagina zelf en verdwijnen daar wel.
 * "Je bent aangemeld en neemt 2 personen mee (1 volwassene en 1 kind)".
 */
export function evenementSamenvattingZonderNamen(
  aanwezig: boolean,
  introducees: ReadonlyArray<{ relatie: EvenementIntroduceeRelatie }>,
): string {
  if (!aanwezig) return 'Je bent er niet bij'
  if (introducees.length === 0) return 'Je bent aangemeld en komt alleen'
  const kinderen = introducees.filter((i) => i.relatie === 'KIND').length
  const volwassenen = introducees.length - kinderen
  const delen: string[] = []
  if (volwassenen > 0) delen.push(`${volwassenen} ${volwassenen === 1 ? 'volwassene' : 'volwassenen'}`)
  if (kinderen > 0) delen.push(`${kinderen} ${kinderen === 1 ? 'kind' : 'kinderen'}`)
  const totaal = introducees.length
  return `Je bent aangemeld en neemt ${totaal} ${totaal === 1 ? 'persoon' : 'personen'} mee (${delen.join(' en ')})`
}
